import * as React from "react";
import { Check, ChevronDown, Plus, X } from "lucide-react";

import { cn } from "@/modern/utils";
import { Button } from "@/modern/components/ui/button";
import { Badge } from "@/modern/components/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/modern/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/modern/components/ui/tooltip";

type TComboValue = number | string;

export type TComboSelectedValue = TComboValue | TComboValue[];

export interface TComboOption {
  label: string;
  value: TComboValue;
  disabled?: boolean;
  icon?: React.ReactNode;
}
interface ComboboxProps {
  options: TComboOption[];
  value?: TComboSelectedValue;
  onValueChange?: (value: TComboSelectedValue | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  multiSelect?: boolean;
  disabled?: boolean;
  className?: string;
  maxDisplay?: number;
  maxWidth?: string;
  showBadges?: boolean;
  emptyValue?: string | null;
  enableRemoveButton?: boolean;
  clearable?: boolean;
  size?: "sm" | "md" | "lg" | "xs";
  buttonSelectedClassName?: string;
  onBeforeSelect?: (option: TComboOption) => boolean | Promise<boolean>;
  showSearch?: boolean;
  popoverContentClassName?: string;
  createOnUnmatched?: (searchValue: string) => void;
  optionItemActionRenderer?: (option: TComboOption) => React.ReactNode;
  renderer?: () => React.ReactNode;
  badgeClassName?: string;
  hideAddButton?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No options found.",
  multiSelect = false,
  disabled = false,
  className,
  maxDisplay = 3,
  maxWidth,
  showBadges = false,
  enableRemoveButton = false,
  clearable = false,
  emptyValue = null,
  size = "md",
  onBeforeSelect,
  showSearch = true,
  buttonSelectedClassName = "",
  popoverContentClassName = "",
  createOnUnmatched,
  optionItemActionRenderer,
  renderer,
  badgeClassName,
  hideAddButton = false,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange
}: ComboboxProps) {
  const defaultValue = React.useMemo(() => {
    if (value !== undefined) return value;
    return multiSelect ? [] : "";
  }, [value, multiSelect]);
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;
  const [searchValue, setSearchValue] = React.useState("");

  const selectedValues = React.useMemo(() => {
    const currentValue = defaultValue;
    if (multiSelect) {
      return Array.isArray(currentValue) ? currentValue : [];
    }
    // Important: `0` is a valid value (e.g. "Never"), so don't treat it as empty.
    if (currentValue === null || currentValue === undefined || currentValue === "") return [];
    return [currentValue];
  }, [defaultValue, multiSelect]);

  const selectedOptions = React.useMemo(() => {
    return options.filter((option) => selectedValues.includes(option.value));
  }, [options, selectedValues]);

  const filteredOptions = React.useMemo(() => {
    return options.filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase()));
  }, [options, searchValue]);

  const handleSelect = async (selectedValue: TComboValue) => {
    const selectedOption = options.find((option) => option.value === selectedValue);
    if (!selectedOption) return;

    if (onBeforeSelect) {
      const shouldProceed = await onBeforeSelect(selectedOption);
      if (!shouldProceed) return;
    }

    if (multiSelect) {
      const currentValues = Array.isArray(defaultValue) ? defaultValue : [];
      const newValues = currentValues.includes(selectedValue) ? currentValues.filter((v) => v !== selectedValue) : [...currentValues, selectedValue];
      onValueChange?.(newValues);
    } else {
      // For single select, only allow clearing if clearable is true
      const newValue = clearable && defaultValue === selectedValue ? emptyValue : selectedValue;
      onValueChange?.(newValue);
      setOpen(false);
    }
  };

  const handleRemove = (valueToRemove: TComboValue) => {
    if (multiSelect) {
      const currentValues = Array.isArray(defaultValue) ? defaultValue : [];
      const newValues = currentValues.filter((v) => v !== valueToRemove);
      onValueChange?.(newValues);
    } else {
      onValueChange?.(emptyValue);
    }
  };

  const handleCreate = () => {
    if (createOnUnmatched && searchValue) {
      createOnUnmatched(searchValue);
      setSearchValue("");
      setOpen(false);
    }
  };

  const isSelected = (optionValue: TComboValue) => {
    return selectedValues.includes(optionValue);
  };

  const remainingCount = multiSelect ? Math.max(0, selectedOptions.length - maxDisplay) : 0;
  const [morePopoverOpen, setMorePopoverOpen] = React.useState(false);
  const moreHoverTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleMoreEnter = () => {
    if (moreHoverTimer.current) clearTimeout(moreHoverTimer.current);
    moreHoverTimer.current = setTimeout(() => setMorePopoverOpen(true), 150);
  };
  const handleMoreLeave = () => {
    if (moreHoverTimer.current) clearTimeout(moreHoverTimer.current);
    moreHoverTimer.current = setTimeout(() => setMorePopoverOpen(false), 150);
  };

  const displayText = React.useMemo(() => {
    if (selectedOptions.length === 0) {
      return placeholder;
    }

    if (!multiSelect) {
      return selectedOptions[0]?.label || placeholder;
    }

    if (selectedOptions.length <= maxDisplay) {
      return selectedOptions.map((option) => option.label).join(", ");
    }

    return `${selectedOptions.length} selected`;
  }, [selectedOptions, placeholder, multiSelect, maxDisplay]);

  const renderBadges = () => {
    const displayOptions = multiSelect ? selectedOptions.slice(0, maxDisplay) : selectedOptions.slice(0, 1);

    return (
      <>
        {displayOptions.map((option) => (
          <Tooltip key={option.value} delayDuration={300}>
            <TooltipTrigger asChild>
              <div className="group/badge min-w-0">
                <Badge size={size} variant="muted" className={cn("cursor-pointer border py-1 px-3 relative", badgeClassName)}>
                  <span className="truncate">{option.label}</span>
                  {enableRemoveButton && (
                    <button
                      type="button"
                      className="absolute right-0.5 top-1/2 -translate-y-1/2 size-4 items-center justify-center rounded-full bg-inherit hover:text-destructive hidden group-hover/badge:inline-flex transition-colors"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemove(option.value);
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <X className="size-3" />
                    </button>
                  )}
                </Badge>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs max-w-[12.5rem]">
              {option.label}
            </TooltipContent>
          </Tooltip>
        ))}
        {remainingCount > 0 && (
          <Popover
            open={morePopoverOpen}
            onOpenChange={(isOpen) => {
              setMorePopoverOpen(isOpen);
              if (isOpen) setOpen(false);
            }}
          >
            <PopoverTrigger asChild>
              <div
                className="flex-shrink-0"
                onMouseEnter={handleMoreEnter}
                onMouseLeave={handleMoreLeave}
              >
                <Badge size={size} variant="secondary" className={cn("cursor-pointer", badgeClassName)}>
                  +{remainingCount} more
                </Badge>
              </div>
            </PopoverTrigger>

            <PopoverContent
              align="start"
              className="p-1 w-56 max-h-60 overflow-y-auto shadow-md rounded-md"
              onMouseEnter={handleMoreEnter}
              onMouseLeave={handleMoreLeave}
            >
              {selectedOptions.slice(maxDisplay).map((option) => (
                <div key={option.value} className="flex items-center gap-2 px-2 py-1.5">
                  <span className="flex-1 truncate text-sm">{option.label}</span>
                </div>
              ))}
            </PopoverContent>
          </Popover>
        )}
        {!hideAddButton && (multiSelect || !selectedOptions.length) && (
          <Badge size={size} variant="primary-outline" className={cn("cursor-pointer", badgeClassName)}>
            <Plus className="h-3 w-3" /> {placeholder}
          </Badge>
        )}
      </>
    );
  };

  const buttonSize = size === "xs" ? "sm" : size === "md" ? "default" : size;
  const buttonHeight = size === "sm" ? "h-9" : size === "lg" ? "h-swivu-input" : "h-swivu-input";

  return (
    <div className={cn("w-full flex-shrink-0", className)} style={{ maxWidth }}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>

          {
            renderer ? <div>{renderer()}</div> : showBadges ? (
              <div className="flex flex-wrap gap-1.5" > {renderBadges()}</div>
            ) : (
              <Button
                variant="outline"
                role="combobox"
                size={buttonSize}
                aria-expanded={open}
                disabled={disabled}
                className={cn("w-full justify-between text-left font-normal", buttonHeight, buttonSelectedClassName)}
              >
                <span className={cn("truncate", selectedOptions.length === 0 && "text-muted-foreground")}>{displayText}</span>
                <div className="flex items-center gap-1">
                  {clearable && selectedOptions.length > 0 && (
                    <button
                      type="button"
                      className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100 flex items-center justify-center"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onValueChange?.(emptyValue);
                        setOpen(false);
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                </div>
              </Button>
            )
          }

        </PopoverTrigger>
        <PopoverContent className={cn(!showBadges && "w-[--radix-popover-trigger-width]", "p-0 z-[--swivu-bryntum-z-index]", popoverContentClassName)} align={showBadges ? "end" : "start"} style={{ width: maxWidth }}>
          <Command shouldFilter={!showSearch}>
            {showSearch && (
              <CommandInput placeholder={searchPlaceholder} className={cn("h-swivu-input", size === "sm" ? "h-9" : size === "lg" ? "h-swivu-input" : "h-swivu-input")} value={searchValue} onValueChange={setSearchValue} />
            )}
            <CommandList>
              {filteredOptions.length === 0 && createOnUnmatched && searchValue ? (
                <CommandGroup>
                  <CommandItem onSelect={handleCreate} className="flex items-center gap-2 group-[command-item]">
                    <Plus className="h-4 w-4" />
                    <span className="flex-1">Create "{searchValue}"</span>
                  </CommandItem>
                </CommandGroup>
              ) : (
                <>
                  <CommandEmpty>{emptyMessage}</CommandEmpty>
                  <CommandGroup>
                    {(showSearch ? filteredOptions : options).map((option) => (
                      <CommandItem key={option.value} value={option.label} disabled={option.disabled} onSelect={() => handleSelect(option.value)} className="flex items-center gap-2 group/command-item">
                        <Check className={cn("h-4 w-4", isSelected(option.value) ? "opacity-100" : "opacity-0")} />
                        {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
                        <span className="flex-1">{option.label}</span>
                        {optionItemActionRenderer && optionItemActionRenderer(option)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div >
  );
}

export default Combobox;
